import { Calendar, Check, ExternalLink, Leaf, TreePine } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const BOOKING_URL_BEAUTY = "https://home.shortcutssoftware.com/ah#/_h/sites/6509/details";

const whyAspect = [
  {
    title: 'Result-Driven Formulations',
    description: 'Over 20 years of research ensuring optimal concentrations of pure active ingredients that deliver visible results without irritation.',
  },
  {
    title: 'Carbon Neutral',
    description: 'Aspect is a certified carbon neutral company, planting trees to offset their footprint. When you use Aspect, you can feel good knowing your skincare choice is helping the environment.',
  },
  {
    title: 'Cosmeceutical Grade',
    description: 'Bridging the gap between cosmetics and pharmaceuticals — active ingredients at concentrations that genuinely transform skin.',
  },
  {
    title: 'Suitable for All',
    description: 'Versatile solutions that deliver visible results for all skin types without complexity or irritation.',
  },
];

const productHighlights = [
  {
    name: 'Cleansers & Exfoliants',
    description: 'From the bestselling Purastat 5 Cleanser to the Fruit Enzyme Mask, Aspect\'s cleansing range prepares your skin perfectly for treatment and active ingredient absorption.',
    benefit: 'Clean, balanced skin',
  },
  {
    name: 'Serums & Actives',
    description: 'Powerful vitamin-infused serums targeting specific concerns — from pigmentation and fine lines to dehydration and dullness. The backbone of an effective skincare routine.',
    benefit: 'Targeted correction',
  },
  {
    name: 'Moisturisers & Oils',
    description: 'Including the cult-favourite Marine Youth Renewal Oil, these formulations lock in hydration and protect the skin barrier while delivering ongoing repair.',
    benefit: 'Deep nourishment',
  },
  {
    name: 'Sun Protection',
    description: 'Essential daily SPF products that protect against UV damage while feeling lightweight and comfortable under makeup — no white cast.',
    benefit: 'Daily defence',
  },
];

export default function AspectSkincare() {
  return (
    <main className="min-h-screen bg-[#FFFDF9]" data-testid="aspect-skincare-page">
      <SEO page="aspectSkincare" />

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1556228578-0d85b1a4d571?w=1920&h=800&fit=crop"
            alt="Aspect Skincare products"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FFFDF9] via-[#FFFDF9]/95 to-[#FFFDF9]/70" />
        </div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#4A5D44]/10 border border-[#4A5D44]/30 rounded-full px-4 py-2 mb-6">
              <TreePine className="w-4 h-4 text-[#4A5D44]" />
              <span className="text-sm text-[#4A5D44] font-medium">Carbon Neutral Skincare</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-[#2C2420] leading-[1.1] mb-6">
              Aspect Skincare
            </h1>
            <p className="text-xl text-[#8D7B68] italic mb-4">
              "Radiant skin, simplified."
            </p>
            <p className="text-lg text-[#5D4A3A] leading-relaxed mb-8">
              At Artistry House, we use and recommend Aspect Skincare for all our facial and skin treatments. A leading Australian cosmeceutical brand dedicated to result-driven formulations that simplify daily skincare — and as a certified carbon neutral company, they're planting trees for a healthier planet too.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={BOOKING_URL_BEAUTY} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#4A5D44] hover:bg-[#3d4e38] text-white rounded-full px-8 py-4 text-lg" data-testid="aspect-book-btn">
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Skin Treatment
                </Button>
              </a>
              <a href="https://www.aspectskincare.com.au/" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-[#3D352D] text-[#3D352D] hover:bg-[#3D352D] hover:text-white rounded-full px-8 py-4 text-lg">
                  <ExternalLink className="w-5 h-5 mr-2" />
                  Visit Aspect Skincare
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Carbon Neutral Banner */}
      <section className="py-16 bg-[#4A5D44] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row items-center gap-8 text-center md:text-left">
            <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
              <TreePine className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-medium mb-2">Feel Good About Your Skincare</h2>
              <p className="text-white/80 text-base md:text-lg">
                Aspect is a certified carbon neutral company, actively planting trees to offset their environmental impact. When you choose Aspect products at Artistry House, you're not just investing in your skin — you're contributing to a healthier planet. Beauty that cares.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Aspect */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">Why We Chose Aspect</p>
            <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">The Aspect Difference</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {whyAspect.map((item) => (
              <div key={item.title} className="bg-white p-8 rounded-lg border border-[#D4C4B0]/50 hover:border-[#4A5D44]/50 transition-colors">
                <h3 className="text-xl font-medium text-[#2C2420] mb-3">{item.title}</h3>
                <p className="text-[#5D4A3A] leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Highlights */}
      <section className="py-24 md:py-32 bg-[#E8DED2]/30">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-[#4A5D44] mb-4">Our Recommended Ranges</p>
            <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">Aspect Products We Use</h2>
            <p className="text-lg text-[#5D4A3A] max-w-2xl mx-auto">
              With over 20 years of research behind each formulation, Aspect products effectively deliver optimal concentrations of pure active ingredients.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {productHighlights.map((product) => (
              <div key={product.name} className="bg-white p-8 rounded-lg border border-[#D4C4B0]/50">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#4A5D44]/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <Check className="w-5 h-5 text-[#4A5D44]" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-[#4A5D44] mb-1">{product.benefit}</p>
                    <h3 className="text-xl font-medium text-[#2C2420] mb-2">{product.name}</h3>
                    <p className="text-sm text-[#5D4A3A] leading-relaxed">{product.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Philosophy */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="aspect-[4/5] rounded-lg overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=600&h=750&fit=crop"
                alt="Skincare facial treatment with Aspect products"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="text-sm uppercase tracking-widest text-[#4A5D44] mb-4">Brand Philosophy</p>
              <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
                Simple, Active, Effective
              </h2>
              <p className="text-lg text-[#5D4A3A] leading-relaxed mb-6">
                Aspect believes that the journey to radiant, healthy-looking skin shouldn't be complicated. That's why they've created a range of expertly developed products designed to simplify your routine while delivering real results.
              </p>
              <p className="text-lg text-[#5D4A3A] leading-relaxed mb-6">
                As a leading cosmeceutical skincare brand, Aspect provides active, nurturing solutions that restore and protect your skin, revealing its natural beauty. Their versatile range delivers visible results without irritation or complexity — for all skin types.
              </p>
              <p className="text-lg text-[#5D4A3A] leading-relaxed">
                The same principles carry across to Aspect DR and Aspect Platinum, which are exclusive to trusted clinic partners like Artistry House. These professional-grade ranges take a 360-degree approach to skin health.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#2C2420] text-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl md:text-4xl font-medium mb-6">
            Ready to Transform Your Skin?
          </h2>
          <p className="text-lg text-[#BCAAA4] max-w-2xl mx-auto mb-8">
            Book a skin consultation at Artistry House. We'll recommend an Aspect skincare routine tailored to your skin goals — because great skin starts with great products.
          </p>
          <a href={BOOKING_URL_BEAUTY} target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#4A5D44] hover:bg-[#3d4e38] text-white rounded-full px-10 py-6 text-lg" data-testid="aspect-book-cta-btn">
              <Calendar className="w-5 h-5 mr-2" />
              Book Skin Consultation
            </Button>
          </a>
        </div>
      </section>
    </main>
  );
}
